import type { LiveDrone } from '@/hooks/useWebSocket';
import { haversineDistanceM } from '@/lib/mission-planning';
import { MIN_CREDIBLE_GROUNDSPEED_MPS } from '@/lib/range-rings';

/**
 * Whether a handover is running normally (`none`) or needs an operator to look (`attention`).
 *
 * ONE ABNORMAL LOOK, NOT A STATE PER CAUSE (D17, the user's call after three review rounds). Every
 * abnormal case - a hold or fault the orchestrator named, a last-resort recall, a coverage read that
 * failed, a relief under manual control or still grounded long after its launch - is drawn the same:
 * a red line with no number, a red ring around both aircraft, and one key row pointing at the
 * Coverage panel, which is where the cause is named. Each per-cause look tried before it opened new
 * edge cases a map with no words could not settle.
 */
export type HandoverAttention = 'none' | 'attention';

/**
 * A continuous-coverage handover as the map draws it (#251): a line from the relief to the aircraft
 * it is relieving, labelled with the time left until the station changes hands.
 *
 * Map-shaped rather than wire-shaped, so the map does not depend on the COP's coverage types:
 * `handoverLinksFrom` in `useCopCoverage` is the one place a `GET /api/coverage` row becomes one.
 * Only the phases BEFORE `handover_done` exist here - at `handover_done` the handover has happened
 * and the line comes down.
 */
export interface HandoverLink {
  actionId: string;
  phase: 'launching' | 'transit' | 'on_station' | 'confirming';
  reliefDroneId: string;
  outgoingDroneId: string;
  /** When the rotation action entered `phase` (its `updated_at`), epoch ms. */
  phaseSinceMs: number;
  /** The planner's cruise speed for this cycle. Null when it had none. */
  cruiseSpeedMs: number | null;
  /**
   * What the coverage read says: a named hold or fault, a last-resort recall, or a read that failed.
   * The map adds the causes only the live picture can see (`handoverNeedsAttention`).
   */
  attention: HandoverAttention;
}

/** Stable identity, so a map with no handovers does not re-run its source effect. */
export const EMPTY_HANDOVER_LINKS: HandoverLink[] = [];

/**
 * How often the orchestrator advances a rotation - `DEFAULT_ORCHESTRATOR_SWEEP_S` in
 * `src/services/coverage-orchestrator.ts`. Each phase move waits for the next sweep.
 */
export const ORCHESTRATOR_SWEEP_S = 5;

/**
 * How long the relief must hold the station before the handover - `DEFAULT_STATION_DWELL_SECONDS`
 * in `src/services/coverage-station.ts`, counted from the moment the action entered `confirming`.
 */
export const STATION_DWELL_S = 10;

/**
 * How long a relief may still be on its pad after `transit` began and be "on its way".
 *
 * `transit` is claimed only after the bay has opened and the mission was dispatched (#747), so a
 * healthy relief lifts within seconds. Past this, nothing in the orchestrator names the state (it
 * has no arrival budget - see the follow-up issue), so the map does: the relief turned back, or
 * never left.
 */
export const RELIEF_LIFT_ALLOWANCE_S = 120;

/** The label while a healthy handover is on its way but not yet countable: time-shaped, and no words. */
export const HANDOVER_TIME_PENDING = '--:--';

/**
 * Is the aircraft in the air? Its own answer when it gives one (DJI `KeyIsFlying`), the
 * altitude-derived status otherwise - absence of `is_flying` is "cannot say", never "on the ground".
 */
export function isAirborne(drone: LiveDrone): boolean {
  return drone.is_flying ?? drone.status === 'flying';
}

/**
 * Does this handover need an operator to look, on everything the map knows right now?
 *
 * The read's own verdict, plus the two causes nothing on the server names: an aircraft of the pair
 * under manual control (Coverage is never told, so its countdown would describe a flight nobody is
 * flying), and a relief still grounded well into `transit`.
 */
export function handoverNeedsAttention(
  link: HandoverLink,
  relief: LiveDrone,
  nowMs: number,
  manualHold: boolean,
): boolean {
  if (link.attention === 'attention' || manualHold) return true;
  if (link.phase !== 'transit' || isAirborne(relief)) return false;
  return (nowMs - link.phaseSinceMs) / 1000 > RELIEF_LIFT_ALLOWANCE_S;
}

/**
 * Seconds until the station changes hands on a NORMAL handover, or null when it cannot be counted
 * yet.
 *
 * Mirrors the orchestrator rather than modelling the flight: the handover fires on the first sweep
 * after the relief has held the station for the dwell.
 *
 *   - `launching`, or `transit` while the relief is still on its pad: null. How long the bay, the
 *     home point and the lift take is not bounded by anything this map can read.
 *   - `transit`, airborne: straight-line distance to the outgoing aircraft over the planner's cruise
 *     speed (the relief's own ground speed when the planner had none), plus a sweep and the dwell.
 *     Computed from where the aircraft are now, so it owes nothing to when the phase began.
 *   - `on_station`: a sweep into `confirming`, then the dwell.
 *   - `confirming`: what is left of the dwell.
 *
 * Never below zero: a wait the orchestrator has not named reads 0:00 rather than negative time.
 */
export function handoverSecondsRemaining(
  link: HandoverLink,
  relief: LiveDrone,
  outgoing: LiveDrone,
  nowMs: number,
): number | null {
  const elapsedS = (nowMs - link.phaseSinceMs) / 1000;
  switch (link.phase) {
    case 'launching':
      return null;
    case 'confirming':
      return Math.max(0, STATION_DWELL_S - elapsedS);
    case 'on_station':
      return Math.max(0, ORCHESTRATOR_SWEEP_S + STATION_DWELL_S - elapsedS);
    case 'transit': {
      if (!isAirborne(relief)) return null;
      // The range rings' bar for a ground speed worth estimating on - below it is a climb or a hover.
      const liveSpeed = Number.isFinite(relief.speed) && relief.speed >= MIN_CREDIBLE_GROUNDSPEED_MPS
        ? relief.speed
        : null;
      const speed = link.cruiseSpeedMs ?? liveSpeed;
      if (speed === null) return null;
      const distanceM = haversineDistanceM(
        { lat: relief.latitude, lng: relief.longitude },
        { lat: outgoing.latitude, lng: outgoing.longitude },
      );
      return distanceM / speed + ORCHESTRATOR_SWEEP_S + STATION_DWELL_S;
    }
  }
}

/**
 * The label on a normal handover: the countdown, or `--:--` while it is on its way but not yet
 * countable (the bay, the lift, or no speed to estimate on). A handover that needs attention gets no
 * label at all - the red line and ring are the message.
 */
export function handoverLabel(link: HandoverLink, relief: LiveDrone, outgoing: LiveDrone, nowMs: number): string {
  const seconds = handoverSecondsRemaining(link, relief, outgoing, nowMs);
  return seconds === null ? HANDOVER_TIME_PENDING : formatHandoverCountdown(seconds);
}

/** `m:ss`, rounded UP so the label never reads 0:00 while a second is still to run. */
export function formatHandoverCountdown(seconds: number): string {
  const whole = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
}
